/* eslint-disable @typescript-eslint/no-explicit-any */
import type { UseMutationOptions } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mediumRepository } from "../repositories/mediumRepository";
import type { QuickMediumSocialSignupPayload } from "../types";
import {
  normalizeMediumSignupError,
  type MediumSignupError,
} from "./useMediumSignupMutation";
import { homeQueryKeys } from "./useHomeQueries";
import { sessionQueryKey } from "./useSessionQuery";

export type QuickMediumSignupResponse = {
  message?: string;
  profileComplete: boolean;
  user?: {
    profile?: Record<string, unknown>;
    terreiro?: {
      id?: string;
      [k: string]: unknown;
    };
    [k: string]: unknown;
  };
  medium?: {
    id?: string;
    [k: string]: unknown;
  };
  terreiro?: {
    id?: string;
    [k: string]: unknown;
  } | null;
  [k: string]: unknown;
};

const toNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const normalizeAvatarUrl = (
  incoming: Record<string, unknown>,
  fallback?: Record<string, unknown>,
): string | undefined => {
  return (
    toNonEmptyString(incoming.avatarUrl) ??
    toNonEmptyString(incoming.imageUrl) ??
    toNonEmptyString(incoming.picture) ??
    (fallback ? toNonEmptyString(fallback.avatarUrl) : undefined)
  );
};

export const useQuickMediumSocialSignupMutation = (
  options?: UseMutationOptions<
    QuickMediumSignupResponse,
    MediumSignupError,
    QuickMediumSocialSignupPayload
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<
    QuickMediumSignupResponse,
    MediumSignupError,
    QuickMediumSocialSignupPayload
  >({
    mutationFn: (data: QuickMediumSocialSignupPayload) =>
      mediumRepository.quickSignupSocial(data).catch((err) => {
        throw normalizeMediumSignupError(err);
      }),
    ...options,
    onSuccess: async (data, variables, context) => {
      try {
        if (data) {
          const profile = data.user?.profile as
            | Record<string, unknown>
            | undefined;
          let normalizedProfile: Record<string, unknown> | undefined;
          const terreiro = data.terreiro;
          const terreiroId = terreiro?.id;
          const medium = data.medium;
          const mediumId = medium?.id;

          // ──────────────────────────────────────────────────────────
          // 1. Update localStorage FIRST so that useSessionQuery and
          //    useCachedTerreiro pick up the new context immediately.
          //    Social signups have no JWT token (authenticated via OAuth cookie).
          // ──────────────────────────────────────────────────────────

          if (profile) {
            const previousAuthProfile = queryClient.getQueryData<
              Record<string, unknown>
            >(["auth", "currentUser"]);
            const avatarUrl = normalizeAvatarUrl(profile, previousAuthProfile);
            normalizedProfile = {
              ...profile,
              ...(avatarUrl ? { avatarUrl } : {}),
            };

            try {
              localStorage.setItem(
                "ilovemacumba:auth:currentUser",
                JSON.stringify(normalizedProfile),
              );
            } catch {
              // ignore localStorage errors
            }
          }

          if (terreiroId && terreiro) {
            try {
              localStorage.setItem(
                "ilovemacumba:terreiro:current",
                JSON.stringify(terreiro),
              );
            } catch {
              // ignore localStorage errors
            }
          }

          if (mediumId && medium) {
            try {
              localStorage.setItem(
                "ilovemacumba:medium:current",
                JSON.stringify(medium),
              );
            } catch {
              // ignore localStorage errors
            }
          }

          // ──────────────────────────────────────────────────────────
          // 2. Pre-populate React Query caches to avoid loading flash.
          // ──────────────────────────────────────────────────────────

          queryClient.setQueryData([...sessionQueryKey, "personal"], data.user);
          if (terreiroId) {
            queryClient.setQueryData(
              [...sessionQueryKey, terreiroId],
              data.user,
            );
          }

          if (normalizedProfile) {
            queryClient.setQueryData(
              ["auth", "currentUser"],
              normalizedProfile,
            );
            queryClient.setQueryData(homeQueryKeys.homeProfile, (prev: any) => ({
              ...(prev && typeof prev === "object" ? prev : {}),
              profile: {
                ...((prev &&
                typeof prev === "object" &&
                prev.profile &&
                typeof prev.profile === "object"
                  ? prev.profile
                  : {}) as Record<string, unknown>),
                ...normalizedProfile,
              },
            }));
          }
          if (terreiroId && terreiro) {
            queryClient.setQueryData(["terreiro", terreiroId], terreiro);
            queryClient.setQueryData(["terreiro", "current"], terreiro);
          }
          if (mediumId && medium) {
            queryClient.setQueryData(["medium", mediumId], medium);
            queryClient.setQueryData(["medium", "current"], medium);
          }
        }

        // Background refetch to confirm session via /v1/auth/me.
        queryClient.invalidateQueries({ queryKey: sessionQueryKey });
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        queryClient.invalidateQueries({ queryKey: ["medium", "me", "status"] });
        queryClient.invalidateQueries({ queryKey: ["medium", "me", "data"] });
      } catch {
        // swallow cache/localStorage errors
      }

      options?.onSuccess?.(data, variables, context as any);
    },
  });
};

export default useQuickMediumSocialSignupMutation;
